import React, { useState, useRef, useCallback } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  X,
  Download,
  Columns,
  SplitSquareHorizontal,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Sparkles,
  Eraser,
  Maximize,
  Replace,
  Palette,
  Shirt,
  Home,
  ImageIcon,
  Wand2,
  GripVertical,
} from 'lucide-react';

interface Transformation {
  type: string;
  [key: string]: any;
}

interface ImageCompareModalProps {
  isOpen: boolean;
  originalUrl: string;
  processedUrl: string;
  filename: string;
  transformations?: Transformation[];
  onClose: () => void;
  onDownload?: (url: string, type: 'original' | 'processed') => void;
  onRevert?: () => void;
}

type ViewMode = 'split' | 'slider';

// Transformation icons config
const TRANSFORMATION_ICONS: Record<string, React.ReactNode> = {
  background_removal: <Eraser className="w-3.5 h-3.5" />,
  upscale: <Maximize className="w-3.5 h-3.5" />,
  generative_fill: <Sparkles className="w-3.5 h-3.5" />,
  generative_remove: <Eraser className="w-3.5 h-3.5" />,
  generative_replace: <Replace className="w-3.5 h-3.5" />,
  generative_recolor: <Palette className="w-3.5 h-3.5" />,
  invisible_mannequin: <Shirt className="w-3.5 h-3.5" />,
  virtual_staging: <Home className="w-3.5 h-3.5" />,
  format_conversion: <ImageIcon className="w-3.5 h-3.5" />,
  gemini_studio_retouch: <Sparkles className="w-3.5 h-3.5" />,
  studio_retouch: <Sparkles className="w-3.5 h-3.5" />,
  resize: <Maximize className="w-3.5 h-3.5" />,
  quality: <Sparkles className="w-3.5 h-3.5" />,
};

const getTransformationLabel = (type: string): string => {
  const labels: Record<string, string> = {
    background_removal: 'Background Removal',
    upscale: 'AI Upscale',
    generative_fill: 'Generative Fill',
    generative_remove: 'Generative Remove',
    generative_replace: 'Generative Replace',
    generative_recolor: 'Recolor',
    invisible_mannequin: 'Invisible Mannequin',
    virtual_staging: 'Virtual Staging',
    format_conversion: 'Format Conversion',
    gemini_studio_retouch: 'Studio Retouch',
    studio_retouch: 'Studio Retouch',
    resize: 'Resize',
    quality: 'Quality Enhancement',
  };
  return labels[type] || type.replace(/_/g, ' ');
};

export function ImageCompareModal({
  isOpen,
  originalUrl,
  processedUrl,
  filename,
  transformations = [],
  onClose,
  onDownload,
  onRevert,
}: ImageCompareModalProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('slider');
  const [zoom, setZoom] = useState(1);
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.25, 3));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.25, 0.5));
  const handleResetZoom = () => setZoom(1);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = e.touches[0].clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  }, []);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black"
      onClick={onClose}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-6 py-4 bg-gradient-to-b from-black via-black/80 to-transparent">
        <div className="flex items-center gap-4">
          <div>
            <h3 className="text-white font-semibold text-lg truncate max-w-md">
              {filename}
            </h3>
            {transformations.length > 0 && (
              <div className="flex items-center gap-2 mt-1">
                <span className="text-white/50 text-xs">Transformations:</span>
                <div className="flex items-center gap-1.5">
                  {transformations.map((t, i) => (
                    <Badge
                      key={i}
                      variant="secondary"
                      className="bg-white/10 text-white/90 border-0 text-xs px-2 py-0.5 gap-1"
                    >
                      {TRANSFORMATION_ICONS[t.type] || <Wand2 className="w-3.5 h-3.5" />}
                      {getTransformationLabel(t.type)}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* View Mode Toggle */}
          <div className="flex items-center bg-white/10 rounded-lg p-1">
            <Button
              variant="ghost"
              size="sm"
              className={`h-8 px-3 gap-2 ${viewMode === 'slider' ? 'bg-white/20 text-white' : 'text-white/60 hover:text-white hover:bg-white/10'}`}
              onClick={(e) => {
                e.stopPropagation();
                setViewMode('slider');
              }}
            >
              <SplitSquareHorizontal className="h-4 w-4" />
              <span className="text-xs font-medium">Slider</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className={`h-8 px-3 gap-2 ${viewMode === 'split' ? 'bg-white/20 text-white' : 'text-white/60 hover:text-white hover:bg-white/10'}`}
              onClick={(e) => {
                e.stopPropagation();
                setViewMode('split');
              }}
            >
              <Columns className="h-4 w-4" />
              <span className="text-xs font-medium">Split</span>
            </Button>
          </div>

          {/* Zoom Controls */}
          <div className="flex items-center bg-white/10 rounded-lg p-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-white/60 hover:text-white hover:bg-white/10"
              onClick={(e) => {
                e.stopPropagation();
                handleZoomOut();
              }}
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
            <button
              className="text-white/80 text-xs w-14 text-center hover:text-white"
              onClick={(e) => {
                e.stopPropagation();
                handleResetZoom();
              }}
            >
              {Math.round(zoom * 100)}%
            </button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-white/60 hover:text-white hover:bg-white/10"
              onClick={(e) => {
                e.stopPropagation();
                handleZoomIn();
              }}
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
          </div>

          {/* Download Buttons */}
          {onDownload && (
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-3 text-white/60 hover:text-white hover:bg-white/10 gap-1.5"
                onClick={(e) => {
                  e.stopPropagation();
                  onDownload(processedUrl, 'processed');
                }}
              >
                <Download className="h-4 w-4" />
                <span className="text-xs">Download</span>
              </Button>
            </div>
          )}

          {/* Close Button */}
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 text-white/60 hover:text-white hover:bg-white/10"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div
        className="absolute inset-0 flex items-center justify-center pt-24 pb-24 px-8"
        onClick={(e) => e.stopPropagation()}
      >
        {viewMode === 'slider' && (
          <div
            ref={containerRef}
            className="relative w-full max-w-6xl h-full max-h-[75vh] cursor-ew-resize select-none"
            onTouchMove={handleTouchMove}
          >
            {/* Before Image (Full) */}
            <div className="absolute inset-0 overflow-hidden rounded-xl">
              <img
                src={originalUrl}
                alt="Before"
                className="w-full h-full object-contain"
                style={{ transform: `scale(${zoom})` }}
                draggable={false}
              />
            </div>

            {/* After Image (Clipped) */}
            <div
              className="absolute inset-0 overflow-hidden rounded-xl"
              style={{ clipPath: `inset(0 0 0 ${sliderPosition}%)` }}
            >
              <img
                src={processedUrl}
                alt="After"
                className="w-full h-full object-contain"
                style={{ transform: `scale(${zoom})` }}
                draggable={false}
              />
            </div>

            {/* Slider Handle */}
            <div
              className="absolute top-0 bottom-0 w-1 bg-white cursor-ew-resize z-10"
              style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
              onMouseDown={handleMouseDown}
            >
              {/* Handle Grip */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center cursor-ew-resize">
                <GripVertical className="w-5 h-5 text-gray-600" />
              </div>

              {/* Labels */}
              <div className="absolute top-4 right-4 bg-black/70 backdrop-blur-sm text-white text-xs font-medium px-3 py-1.5 rounded-full">
                Before
              </div>
              <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm text-gray-900 text-xs font-medium px-3 py-1.5 rounded-full">
                After
              </div>
            </div>

            {/* Corner Labels */}
            <div className="absolute bottom-4 left-4 bg-black/60 backdrop-blur-sm text-white/80 text-xs px-3 py-1.5 rounded-full">
              Original
            </div>
            <div className="absolute bottom-4 right-4 bg-emerald-500/80 backdrop-blur-sm text-white text-xs px-3 py-1.5 rounded-full flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" />
              Processed
            </div>
          </div>
        )}

        {viewMode === 'split' && (
          <div className="flex gap-6 w-full max-w-7xl h-full max-h-[75vh]">
            {/* Before Panel */}
            <div className="flex-1 flex flex-col">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-gray-500" />
                  <span className="text-white font-medium">Before</span>
                </div>
                {onDownload && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-white/50 hover:text-white hover:bg-white/10 text-xs"
                    onClick={() => onDownload(originalUrl, 'original')}
                  >
                    <Download className="h-3.5 w-3.5 mr-1" />
                    Download
                  </Button>
                )}
              </div>
              <div className="flex-1 relative overflow-hidden rounded-xl bg-black/50 border border-white/10">
                <img
                  src={originalUrl}
                  alt="Before"
                  className="w-full h-full object-contain"
                  style={{ transform: `scale(${zoom})` }}
                />
              </div>
            </div>

            {/* Divider */}
            <div className="flex flex-col items-center justify-center gap-2">
              <div className="w-px h-16 bg-white/20" />
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                <Columns className="w-5 h-5 text-white/60" />
              </div>
              <div className="w-px h-16 bg-white/20" />
            </div>

            {/* After Panel */}
            <div className="flex-1 flex flex-col">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-emerald-500" />
                  <span className="text-white font-medium">After</span>
                  {transformations.length > 0 && (
                    <span className="text-white/40 text-sm">
                      ({transformations.length} transformation{transformations.length > 1 ? 's' : ''})
                    </span>
                  )}
                </div>
                {onDownload && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 text-xs"
                    onClick={() => onDownload(processedUrl, 'processed')}
                  >
                    <Download className="h-3.5 w-3.5 mr-1" />
                    Download
                  </Button>
                )}
              </div>
              <div className="flex-1 relative overflow-hidden rounded-xl bg-black/50 border border-emerald-500/30">
                <img
                  src={processedUrl}
                  alt="After"
                  className="w-full h-full object-contain"
                  style={{ transform: `scale(${zoom})` }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="absolute bottom-0 left-0 right-0 z-20 flex items-center justify-center gap-4 px-6 py-4 bg-gradient-to-t from-black via-black/80 to-transparent">
        {onRevert && (
          <Button
            variant="outline"
            size="sm"
            className="text-orange-400 border-orange-500/30 bg-orange-500/10 hover:bg-orange-500/20 hover:text-orange-300"
            onClick={(e) => {
              e.stopPropagation();
              onRevert();
            }}
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Revert to Original
          </Button>
        )}

        <div className="text-white/40 text-xs">
          {viewMode === 'slider' ? 'Drag the slider to compare' : 'Side by side comparison'}
        </div>
      </div>
    </div>
  );
}
